import { PositionType, SpaceType } from 'data/types';
import { edgeForOutwardNormal } from 'utils/edge-exposure';
import { isPointInPolygon } from 'utils/footprint';
import { isValidFootprintShape } from 'utils/footprint-union';
import {
  EdgeCardinal,
  getSpaceVertices,
  polygonsOverlapStrict
} from 'utils/space-polygon';
import {
  buildWallSegments,
  clampCenterOffsetOnSegment,
  flushCenterOnSegment,
  nearestSegment,
  pointAlongSegment,
  projectPointOnWallSegment,
  segmentRotationDeg,
  unclampedOffsetOnSegment
} from 'utils/wall-segment';
import { WallSegmentType } from 'utils/wall-segment-types';
import {
  InsideWallSnapOptionsType,
  OutsideWallSnapOptionsType,
  SnapPolygonType,
  WallSnapCandidateType,
  WallSnapPlacementType
} from 'utils/wall-snap-types';

const PROBE_EPS_FT = 0.05;
const RAY_START_EPS_FT = 1e-4;
const SPAN_INSET_FT = 0.01;
const MOVE_OUTSIDE_EXTRA_ATTEMPTS = 4;
const OVERLAP_SHRINK_FT = 0.01;

export function spaceSnapPolygon(space: SpaceType): SnapPolygonType | null {
  if (!space.position) {
    return null;
  }
  const relative = getSpaceVertices(space);
  if (relative.length < 3) {
    return null;
  }
  const { x, y } = space.position;
  return {
    id: space.uniqueId,
    vertices: relative.map((vertex) => ({ x: vertex.x + x, y: vertex.y + y })),
    openTo: space.openTo
  };
}

export function spaceSnapPolygons(spaces: SpaceType[]): SnapPolygonType[] {
  return spaces.flatMap((space) => {
    const polygon = spaceSnapPolygon(space);
    return polygon ? [polygon] : [];
  });
}

export function squarePolygon(
  center: PositionType,
  sizeFt: number,
  rotationRad: number = 0
): PositionType[] {
  const half = sizeFt / 2;
  const cos = Math.cos(rotationRad);
  const sin = Math.sin(rotationRad);
  const corners = [
    { x: -half, y: -half },
    { x: half, y: -half },
    { x: half, y: half },
    { x: -half, y: half }
  ];
  return corners.map((corner) => ({
    x: center.x + corner.x * cos - corner.y * sin,
    y: center.y + corner.x * sin + corner.y * cos
  }));
}

export function cardinalForSegment(segment: WallSegmentType): EdgeCardinal {
  return segment.cardinal ?? edgeForOutwardNormal(segment.outwardNormal);
}

function placementFromCenter(
  segment: WallSegmentType,
  center: PositionType,
  sizeFt: number
): WallSnapPlacementType {
  return {
    x: center.x - sizeFt / 2,
    y: center.y - sizeFt / 2,
    center,
    edge: cardinalForSegment(segment),
    edgeIndex: segment.edgeIndex,
    rotationDeg: segmentRotationDeg(segment)
  };
}

function probeOutside(
  segment: WallSegmentType,
  offsetFt: number,
  distanceFt: number = PROBE_EPS_FT
): PositionType {
  const onWall = pointAlongSegment(segment, offsetFt);
  return {
    x: onWall.x + segment.outwardNormal.x * distanceFt,
    y: onWall.y + segment.outwardNormal.y * distanceFt
  };
}

function isPointInAnyPolygon(
  point: PositionType,
  polygons: SnapPolygonType[],
  excludeId?: string
): boolean {
  return polygons.some(
    (polygon) =>
      polygon.id !== excludeId && isPointInPolygon(point, polygon.vertices)
  );
}

function rayHitDistance(
  origin: PositionType,
  direction: PositionType,
  a: PositionType,
  b: PositionType
): number | null {
  const sx = b.x - a.x;
  const sy = b.y - a.y;
  const denom = direction.x * sy - direction.y * sx;
  if (Math.abs(denom) < 1e-12) {
    return null;
  }
  const wx = a.x - origin.x;
  const wy = a.y - origin.y;
  const t = (wx * sy - wy * sx) / denom;
  const u = (wx * direction.y - wy * direction.x) / denom;
  if (t < 0 || u < 0 || u > 1) {
    return null;
  }
  return t;
}

export function inwardDepthAtOffset(
  polygon: PositionType[],
  segment: WallSegmentType,
  offsetFt: number
): number {
  const inward = {
    x: -segment.outwardNormal.x,
    y: -segment.outwardNormal.y
  };
  const origin = pointAlongSegment(segment, offsetFt);
  let depth = Infinity;
  polygon.forEach((vertex, index) => {
    if (index !== segment.edgeIndex) {
      const next = polygon[(index + 1) % polygon.length];
      const hit = rayHitDistance(origin, inward, vertex, next);
      if (hit !== null && hit > RAY_START_EPS_FT && hit < depth) {
        depth = hit;
      }
    }
  });
  return depth;
}

function insideCenterOnSegment(
  polygon: PositionType[],
  segment: WallSegmentType,
  offsetFt: number,
  sizeFt: number
): PositionType {
  const depth = inwardDepthAtOffset(polygon, segment, offsetFt);
  const inset = depth < sizeFt ? depth / 2 : sizeFt / 2;
  const onWall = pointAlongSegment(segment, offsetFt);
  return {
    x: onWall.x - segment.outwardNormal.x * inset,
    y: onWall.y - segment.outwardNormal.y * inset
  };
}

function isBlockedByOpenNeighbor(
  segment: WallSegmentType,
  offsetFt: number,
  sizeFt: number,
  polygon: SnapPolygonType,
  neighbors: SnapPolygonType[]
): boolean {
  const openIds = polygon.openTo ?? [];
  if (openIds.length === 0) {
    return false;
  }
  const openNeighbors = neighbors.filter((neighbor) =>
    openIds.includes(neighbor.id)
  );
  if (openNeighbors.length === 0) {
    return false;
  }
  const half = sizeFt / 2;
  const sampleOffsets = [
    offsetFt - half + SPAN_INSET_FT,
    offsetFt,
    offsetFt + half - SPAN_INSET_FT
  ];
  return sampleOffsets.some((sample) =>
    isPointInAnyPolygon(probeOutside(segment, sample), openNeighbors)
  );
}

function isExteriorAtOffset(
  segment: WallSegmentType,
  offsetFt: number,
  polygonId: string,
  neighbors: SnapPolygonType[]
): boolean {
  return (
    segment.isExterior &&
    !isPointInAnyPolygon(probeOutside(segment, offsetFt), neighbors, polygonId)
  );
}

export function snapInsideCandidates(
  center: PositionType,
  sizeFt: number,
  polygon: SnapPolygonType,
  options: InsideWallSnapOptionsType = {}
): WallSnapCandidateType[] {
  const neighbors = options.neighbors ?? [];
  const segments = buildWallSegments(
    polygon.vertices,
    options.footprints ?? null
  );
  return segments
    .map((segment) => {
      const offsetFt = clampCenterOffsetOnSegment(
        segment,
        unclampedOffsetOnSegment(segment, center),
        sizeFt
      );
      const snappedCenter = insideCenterOnSegment(
        polygon.vertices,
        segment,
        offsetFt,
        sizeFt
      );
      return {
        ...placementFromCenter(segment, snappedCenter, sizeFt),
        segment,
        distanceFt: projectPointOnWallSegment(segment, center).distanceFt,
        isExterior: isExteriorAtOffset(
          segment,
          offsetFt,
          polygon.id,
          neighbors
        ),
        blockedByOpening: isBlockedByOpenNeighbor(
          segment,
          offsetFt,
          sizeFt,
          polygon,
          neighbors
        )
      };
    })
    .sort((a, b) => a.distanceFt - b.distanceFt);
}

function stripCandidate(
  candidate: WallSnapCandidateType
): WallSnapPlacementType {
  const { x, y, center, edge, edgeIndex, rotationDeg } = candidate;
  return { x, y, center, edge, edgeIndex, rotationDeg };
}

export function snapInsidePolygon(
  center: PositionType,
  sizeFt: number,
  polygon: SnapPolygonType,
  options: InsideWallSnapOptionsType = {}
): WallSnapPlacementType | null {
  const candidates = snapInsideCandidates(center, sizeFt, polygon, options);
  if (candidates.length === 0) {
    return null;
  }
  if (options.preferExterior) {
    const exterior = candidates.find((candidate) => candidate.isExterior);
    if (exterior) {
      return stripCandidate(exterior);
    }
  }
  const unblocked = candidates.find((candidate) => !candidate.blockedByOpening);
  return stripCandidate(unblocked ?? candidates[0]);
}

function outsideCandidate(
  segment: WallSegmentType,
  center: PositionType,
  sizeFt: number,
  gapFt: number
): WallSnapCandidateType {
  const offsetFt = clampCenterOffsetOnSegment(
    segment,
    unclampedOffsetOnSegment(segment, center),
    sizeFt
  );
  const flush = flushCenterOnSegment(segment, offsetFt, sizeFt, 'outside');
  const snappedCenter = {
    x: flush.center.x + segment.outwardNormal.x * gapFt,
    y: flush.center.y + segment.outwardNormal.y * gapFt
  };
  return {
    ...placementFromCenter(segment, snappedCenter, sizeFt),
    segment,
    distanceFt: projectPointOnWallSegment(segment, center).distanceFt,
    isExterior: true,
    blockedByOpening: false
  };
}

export function exteriorSnapSegments(
  polygons: SnapPolygonType[],
  options: OutsideWallSnapOptionsType = {}
): WallSegmentType[] {
  const blockers = options.blockers ?? polygons;
  const footprints = options.footprints ?? null;
  const fromPolygons = polygons.flatMap((polygon) =>
    buildWallSegments(polygon.vertices, footprints).filter(
      (segment) =>
        segment.isExterior &&
        !isPointInAnyPolygon(
          probeOutside(segment, segment.lengthFt / 2),
          blockers,
          polygon.id
        )
    )
  );
  const fromFootprints = (footprints ?? [])
    .filter(isValidFootprintShape)
    .flatMap((footprint) => buildWallSegments(footprint, null));
  return [...fromPolygons, ...fromFootprints];
}

export function snapOutsidePolygons(
  center: PositionType,
  sizeFt: number,
  polygons: SnapPolygonType[],
  options: OutsideWallSnapOptionsType = {}
): WallSnapPlacementType | null {
  const gapFt = options.gapFt ?? 0;
  const candidates = exteriorSnapSegments(polygons, options)
    .map((segment) => outsideCandidate(segment, center, sizeFt, gapFt))
    .sort((a, b) => a.distanceFt - b.distanceFt);
  return candidates.length > 0 ? stripCandidate(candidates[0]) : null;
}

export function distanceFromSquareToNearestExteriorWall(
  center: PositionType,
  sizeFt: number,
  polygons: SnapPolygonType[],
  options: OutsideWallSnapOptionsType = {}
): number {
  const nearest = nearestSegment(
    exteriorSnapSegments(polygons, options),
    center
  );
  if (!nearest) {
    return Infinity;
  }
  return Math.max(0, nearest.projection.distanceFt - sizeFt / 2);
}

export function polygonsOverlappingSquare(
  center: PositionType,
  sizeFt: number,
  polygons: SnapPolygonType[],
  rotationRad: number = 0
): SnapPolygonType[] {
  const square = squarePolygon(
    center,
    Math.max(0, sizeFt - OVERLAP_SHRINK_FT),
    rotationRad
  );
  return polygons.filter((polygon) =>
    polygonsOverlapStrict(square, polygon.vertices)
  );
}

export function isSquareInsideAnyPolygon(
  center: PositionType,
  sizeFt: number,
  polygons: SnapPolygonType[],
  rotationRad: number = 0
): boolean {
  return (
    polygonsOverlappingSquare(center, sizeFt, polygons, rotationRad).length > 0
  );
}

function pushedOutsideSegment(
  segment: WallSegmentType,
  onWall: PositionType,
  sizeFt: number
): PositionType {
  return {
    x: onWall.x + (segment.outwardNormal.x * sizeFt) / 2,
    y: onWall.y + (segment.outwardNormal.y * sizeFt) / 2
  };
}

export function moveSquareOutsidePolygons(
  center: PositionType,
  sizeFt: number,
  polygons: SnapPolygonType[]
): PositionType {
  let current = center;
  let rotationRad = 0;
  const maxAttempts = polygons.length + MOVE_OUTSIDE_EXTRA_ATTEMPTS;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const [hit] = polygonsOverlappingSquare(
      current,
      sizeFt,
      polygons,
      rotationRad
    );
    if (!hit) {
      return current;
    }
    const nearest = nearestSegment(
      buildWallSegments(hit.vertices, null),
      current
    );
    if (!nearest) {
      return current;
    }
    current = pushedOutsideSegment(
      nearest.segment,
      nearest.projection.point,
      sizeFt
    );
    rotationRad = nearest.segment.angleRad;
  }
  return current;
}
